import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { CommonLibService } from '@lib/services';
import { FormControl, FormGroup } from '@angular/forms';
import { SupportComponent } from '@lib/components/support-class';
import { ServiceRequestPaymentInputService } from '@common/services/service-request-payment-input/service-request-payment-input-service';
import { GetOrderResponseData, Traveler } from 'src/sdk-servicing';
import { EMAIL_DESTINATION } from '@common/helpers/constants/service-request-payment-input/service-request-payment-input.constants';
import {
  addNotificationEmailFormControlToFormGroup,
  updateConfirmEmailValidator,
} from '@app/service-request-payment-input/presenter/service-request-payment-input-pres.form';
import { concatenateTravelerNames } from '@common/helpers';
import { AswValidators } from '@lib/helpers';

@Component({
  selector: 'asw-service-request-payment-input-notification-email',
  templateUrl: './service-request-payment-input-notification-email.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceRequestPaymentInputNotificationEmailComponent extends SupportComponent {
  @Input() traveler!: Traveler;

  @Input() pnrData!: GetOrderResponseData;

  // [8.5] 搭乗情報連絡先メールアドレス入力エリア
  public isDisplayPassengerEmailInputArea: boolean = false;

  public isShownEmailAsLabel: boolean = false;

  get paymentInputFG(): FormGroup {
    return this._serviceRequestPaymentInputService.paymentInputFG;
  }

  EMAIL_DESTINATION = EMAIL_DESTINATION;
  get id(): string {
    return `asw-service-request-payment-input-notification-email-${this.traveler.id}`;
  }

  // [8.4] 搭乗者名
  // 当該travelers.names.title(搭乗者敬称)+ “ “(半角スペース)+.firstName(搭乗者名)+ “ “(半角スペース)+.names.lastName(搭乗者姓)のフォーマットで表示する。
  get travelerName(): string {
    const travelerNames = this.traveler.names?.[0];
    return concatenateTravelerNames(travelerNames ?? {});
  }

  constructor(
    private readonly _serviceRequestPaymentInputService: ServiceRequestPaymentInputService,
    private readonly _common: CommonLibService,
    private readonly _changeDetectorRef: ChangeDetectorRef
  ) {
    super(_common);
  }

  init(): void {
    this._serviceRequestPaymentInputService.checkValid$.subscribe((checkValid) => {
      if (checkValid) {
        this._changeDetectorRef.detectChanges();
      }
    });

    // (14.1.1.1.14) 搭乗情報メール送信先
    const emailDestinationFC = new FormControl(EMAIL_DESTINATION.SAME_AS_APPLICATION_COMPLETION_CONTACT_EMAIL_ADDRESS, [
      AswValidators.required({
        errorMsgId: 'E0002',
        params: {
          key: 0,
          value: 'message.recipientOfMailAboutBoardingForServiceRequest',
        },
      }),
    ]);
    this.paymentInputFG.addControl('emailDestination_' + this.traveler.id!, emailDestinationFC);
  }

  // (11) 搭乗情報メール送信先ラジオボタン切り替え処理
  onChangeEmailDestination() {
    // (11.1) 選択した搭乗情報メール送付信先により、画面.搭乗情報連絡先メールアドレス入力エリアを「表示内容」シートの当該項目に従って表示する。
    this.isDisplayPassengerEmailInputArea =
      this.paymentInputFG.get('emailDestination_' + this.traveler.id)?.value === EMAIL_DESTINATION.SEND_TO_INDIVIDUAL;
    // [8.7.3] 上記以外、空欄を表示する。
    let emailAddressConfirmValue = '';
    // [8.8.3] 上記以外、""""空欄を表示する

    let emailAddressValue = '';
    if (this.isDisplayPassengerEmailInputArea) {
      // [8.7] 搭乗者メールアドレス
      // [8.8] 搭乗者メールアドレス(確認用)
      // 以下に示す順序に従い、最初に合致するものを初期表示する。
      /**
       * [8.7.1] PNR情報取得API応答.data.contacts.<当該travelers.id>.emails[0].address が存在するの場合、
       * [8.8.1] PNR情報取得API応答.data.contacts.<当該travelers.id>.emails[0].address が存在する場合、
       * PNR情報取得API応答.data.contacts.<当該travelers.id>.emails[0].addressを表示する。
       */
      if (this.pnrData?.contacts?.[this.traveler.id ?? '']?.emails?.[0]?.address) {
        emailAddressValue = this.pnrData?.contacts?.[this.traveler.id ?? '']?.emails[0].address;
        emailAddressConfirmValue = this.pnrData?.contacts?.[this.traveler.id ?? '']?.emails[0].address;

        // ※複数登録されている場合、PNR情報取得API応答.data.contacts.<当該travelers.id>.emails[0].addressをラベルで表示
        if (this.pnrData?.contacts?.[this.traveler.id ?? '']?.emails.length > 1) {
          this.isShownEmailAsLabel = true;
        }
      } else if (this.pnrData?.contacts?.[this.traveler.id ?? '']?.contactEmails?.length === 1) {
        /**
         * [8.7.2] PNR情報取得API応答.data.contacts.<当該travelers.id>.contactEmailsの要素数 = 1件の場合、
         * [8.8.2] PNR情報取得API応答.data.contacts.<当該travelers.id>.contactEmailsの要素数 ＝ 1件の場合、
         * PNR情報取得API応答.data.contacts.<当該travelers.id>.contactEmails[0].addressを表示する。
         */
        emailAddressValue = this.pnrData?.contacts?.[this.traveler.id ?? '']?.contactEmails[0].address;
        emailAddressConfirmValue = this.pnrData?.contacts?.[this.traveler.id ?? '']?.contactEmails[0].address;
      }

      // add validation when select option1
      addNotificationEmailFormControlToFormGroup(
        this.paymentInputFG,
        this.traveler.id!,
        emailAddressValue,
        emailAddressConfirmValue
      );
    } else {
      // add validation when select option2
      this.paymentInputFG.removeControl('emailAddress_' + this.traveler.id);
      this.paymentInputFG.removeControl('emailAddressConfirm_' + this.traveler.id);
    }
  }

  // (14.1.1.2.3) 搭乗者メールアドレスが活性する場合、搭乗者メールアドレスと搭乗者メールアドレス(確認用)が一致しない場合、E0461を表示する。
  emailChange() {
    updateConfirmEmailValidator(
      this.paymentInputFG,
      `emailAddress_${this.traveler.id}`,
      `emailAddressConfirm_${this.traveler.id}`
    );
  }

  destroy(): void {
    /* override abstract destroy function of base class */
  }

  reload(): void {
    /* override abstract reload function of base class */
  }
}
